import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  LinearProgress,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import BadgeIcon from '@mui/icons-material/Badge';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import SyncIcon from '@mui/icons-material/Sync';
import RouterIcon from '@mui/icons-material/Router';

import MigracionOltTab from '../components/MigracionOltTab';

import {
  useImportSoftnetRango,
  useImportSoftnetCedulas,
  useSoftnetMigratedHistory,
  useSyncDatosExtendidos,
  type IImportSoftnetResponse,
  type IImportSoftnetClienteMigrado,
  type IImportSoftnetAccion,
  type ISoftnetMigratedClient,
  type ISyncDatosExtendidosResponse,
} from '@/shared/actions/erp/tickets-soporte-cliente/softnet-import.actions';
import { useAuthStore } from '@/store/auth/auth.store';
import { useFetchUsuarioResumen } from '@/shared/actions/erp/tickets-soporte-cliente';

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const parseCedulas = (raw: string): string[] => {
  return raw
    .split(/[\s,;\n\r\t]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

interface ResumenCardProps {
  label: string;
  value: number;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
}

const ResumenCard: React.FC<ResumenCardProps> = ({ label, value, color = 'primary' }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" color={`${color}.main`} sx={{ fontWeight: 600, mt: 0.5 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const ResumenMigracion: React.FC<{ data: IImportSoftnetResponse }> = ({ data }) => {
  const tieneErrores = data.errores > 0;

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        {tieneErrores ? (
          <ErrorOutlineIcon color="warning" />
        ) : (
          <CheckCircleIcon color="success" />
        )}
        <Typography variant="h6">Resumen de la última corrida</Typography>
        <Chip
          size="small"
          label={`${(data.duracion_ms / 1000).toFixed(2)}s`}
          variant="outlined"
        />
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={3}>
          <ResumenCard label="Solicitados" value={data.solicitados} color="info" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <ResumenCard label="Procesados" value={data.procesados} color="primary" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <ResumenCard label="Clientes nuevos" value={data.clientes_nuevos} color="success" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <ResumenCard
            label="Clientes actualizados"
            value={data.clientes_actualizados}
            color="info"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <ResumenCard label="Contratos nuevos" value={data.contratos_nuevos} color="success" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <ResumenCard
            label="Contratos actualizados"
            value={data.contratos_actualizados}
            color="info"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <ResumenCard
            label="Tickets históricos nuevos"
            value={data.tickets_historicos_nuevos}
            color="success"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <ResumenCard
            label="Sin contratos"
            value={data.clientes_sin_contratos}
            color="warning"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <ResumenCard
            label="Errores"
            value={data.errores}
            color={tieneErrores ? 'error' : 'success'}
          />
        </Grid>
      </Grid>

      {tieneErrores && data.errores_detalle && data.errores_detalle.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Detalle de errores
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 200 }}>Identificación</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Mensaje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.errores_detalle.map((err, idx) => {
                  const errObj = typeof err === 'string'
                    ? { identificacion: null, mensaje: err }
                    : err;
                  return (
                    <TableRow key={idx}>
                      <TableCell>{errObj.identificacion || '—'}</TableCell>
                      <TableCell>{errObj.mensaje}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {data.clientes_migrados && data.clientes_migrados.length > 0 && (
        <ClientesMigradosTable clientes={data.clientes_migrados} />
      )}
    </Box>
  );
};

const accionConfig: Record<
  IImportSoftnetAccion,
  { label: string; color: 'success' | 'info' | 'warning' }
> = {
  nuevo: { label: 'Nuevo', color: 'success' },
  actualizado: { label: 'Actualizado', color: 'info' },
  sin_contratos: { label: 'Sin contratos', color: 'warning' },
};

const ClientesMigradosTable: React.FC<{ clientes: IImportSoftnetClienteMigrado[] }> = ({
  clientes,
}) => {
  const [filtro, setFiltro] = useState<'todos' | IImportSoftnetAccion>('todos');

  const filtrados = useMemo(() => {
    if (filtro === 'todos') return clientes;
    return clientes.filter((c) => c.accion === filtro);
  }, [clientes, filtro]);

  const conteos = useMemo(() => {
    const c = { nuevo: 0, actualizado: 0, sin_contratos: 0 };
    clientes.forEach((cl) => {
      c[cl.accion] = (c[cl.accion] || 0) + 1;
    });
    return c;
  }, [clientes]);

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">Clientes migrados</Typography>
        <Chip label={`${clientes.length} total`} size="small" variant="outlined" />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip
          label={`Todos (${clientes.length})`}
          color={filtro === 'todos' ? 'primary' : 'default'}
          variant={filtro === 'todos' ? 'filled' : 'outlined'}
          onClick={() => setFiltro('todos')}
          size="small"
        />
        <Chip
          label={`Nuevos (${conteos.nuevo})`}
          color={filtro === 'nuevo' ? 'success' : 'default'}
          variant={filtro === 'nuevo' ? 'filled' : 'outlined'}
          onClick={() => setFiltro('nuevo')}
          size="small"
        />
        <Chip
          label={`Actualizados (${conteos.actualizado})`}
          color={filtro === 'actualizado' ? 'info' : 'default'}
          variant={filtro === 'actualizado' ? 'filled' : 'outlined'}
          onClick={() => setFiltro('actualizado')}
          size="small"
        />
        <Chip
          label={`Sin contratos (${conteos.sin_contratos})`}
          color={filtro === 'sin_contratos' ? 'warning' : 'default'}
          variant={filtro === 'sin_contratos' ? 'filled' : 'outlined'}
          onClick={() => setFiltro('sin_contratos')}
          size="small"
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Identificación</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Acción</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Contratos</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Equipos</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Tickets hist.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtrados.map((c, idx) => {
              const cfg = accionConfig[c.accion];
              return (
                <TableRow key={`${c.identificacion}-${idx}`} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {c.identificacion || '—'}
                  </TableCell>
                  <TableCell>{c.nombre || '—'}</TableCell>
                  <TableCell>
                    <Chip label={cfg.label} size="small" color={cfg.color} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    {c.contratos}
                    {c.contratos_nuevos > 0 && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="success.main"
                        sx={{ ml: 0.5 }}
                      >
                        (+{c.contratos_nuevos})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{c.equipos}</TableCell>
                  <TableCell align="right">{c.tickets_historicos}</TableCell>
                </TableRow>
              );
            })}
            {filtrados.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Sin resultados para el filtro seleccionado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-EC', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface HistorialMigradosTabProps {
  active: boolean;
}

const HistorialMigradosTab: React.FC<HistorialMigradosTabProps> = ({ active }) => {
  const [page, setPage] = useState<number>(1);
  const pageSize = 20;
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchDebounced, setSearchDebounced] = useState<string>('');

  // Debounce sencillo de 350ms para no pegarle al endpoint en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useSoftnetMigratedHistory({
    page,
    page_size: pageSize,
    search: searchDebounced,
    enabled: active,
  });

  const resp = query.data as
    | { data?: ISoftnetMigratedClient[]; total?: number; pages?: number }
    | undefined;
  const items = resp?.data ?? [];
  const total = resp?.total ?? 0;
  const totalPages = resp?.pages ?? 0;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Buscar por identificación o razón social"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Chip
          label={query.isFetching ? 'Cargando…' : `${total} registro(s)`}
          variant="outlined"
          color={query.isFetching ? 'default' : 'primary'}
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Cédula / RUC</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Razón social</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Ciudad</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Contratos</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Tickets</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Tooltip title="Tickets marcados como históricos (sistema_origen=softnet)">
                  <span>Hist.</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Fecha migración</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Última actualización</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!query.isLoading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {searchDebounced
                    ? 'Sin resultados para el filtro aplicado'
                    : 'Aún no hay clientes migrados desde Softnet'}
                </TableCell>
              </TableRow>
            )}
            {!query.isLoading &&
              items.map((c) => (
                <TableRow key={c.idCliente} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{c.codigo}</TableCell>
                  <TableCell>{c.tipoIdentificacion || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{c.identificacion || '—'}</TableCell>
                  <TableCell>{c.razonSocial || '—'}</TableCell>
                  <TableCell>{c.ciudad || '—'}</TableCell>
                  <TableCell align="right">{c.totalContratos ?? 0}</TableCell>
                  <TableCell align="right">{c.totalTickets ?? 0}</TableCell>
                  <TableCell align="right">{c.totalTicketsHistoricos ?? 0}</TableCell>
                  <TableCell>{formatDateTime(c.fechaMigracion)}</TableCell>
                  <TableCell>{formatDateTime(c.fechaUltimaActualizacion)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            size="small"
          />
        </Stack>
      )}
    </Box>
  );
};

const MigracionSoftnetPage: React.FC = () => {
  // Permisos del modulo migracion: ver/ejecutar (migracion V1_12_0).
  // Si el usuario NO tiene migracion.ver, mostramos un acceso denegado.
  // Si tiene migracion.ver pero NO migracion.ejecutar_importacion, los
  // botones de importar / sync quedan ocultos.
  // Retrocompat: si nadie tiene permisos del modulo (rol antiguo, migracion
  // sin aplicar), permitimos acceso para no romper UI existente.
  const user = useAuthStore((s) => s.user);
  const isSuperUser = !!(user as any)?.is_superuser;
  const currentUserId = (user as any)?.user_id || (user as any)?.user || 0;
  const { data: _resumenRes } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _r = _resumenRes as any;
  const userPermisos: string[] =
    _r?.data?.data?.permisos || _r?.data?.permisos || _r?.permisos || [];
  const tieneFinoMigracion = userPermisos.some((p) => p.startsWith('migracion.'));
  const canVer = isSuperUser || !tieneFinoMigracion || userPermisos.includes('migracion.ver');
  const canEjecutar =
    isSuperUser
    || !tieneFinoMigracion
    || userPermisos.includes('migracion.ejecutar_importacion');

  /**
   * La migración por OLT / ONT tiene permiso propio.
   *
   * No es lo mismo importar clientes desde Softnet —que lee y guarda datos—
   * que la migración por OLT, que DESCONFIGURA la ONT del cliente y la vuelve
   * a activar: lo deja sin servicio mientras corre. Se administra desde
   * /parametrizacion/gestion-accesos, módulo "migracion".
   */
  const canEjecutarOlt =
    isSuperUser
    || !tieneFinoMigracion
    || userPermisos.includes('migracion.ejecutar_olt');

  /**
   * Qué pestañas ve cada quien.
   *
   * `migracion.ver` sigue siendo la llave del módulo; estos cuatro deciden qué
   * hay adentro, porque hay gente que solo trabaja una (el caso que lo motivó:
   * operadores que solo hacen la migración por OLT / ONT).
   *
   * Si el usuario no tiene NINGUNO de los cuatro se le muestran todas: un rol
   * viejo, o un despliegue donde todavía no se asignaron, no puede quedarse
   * con la pantalla vacía. Es la misma regla de retrocompatibilidad que ya
   * usaba `tieneFinoMigracion`.
   */
  const PERMISOS_TAB = {
    rango: 'migracion.ver_rango',
    cedulas: 'migracion.ver_cedulas',
    historial: 'migracion.ver_historial',
    olt: 'migracion.ver_olt',
  } as const;
  const tieneAlgunVerTab = Object.values(PERMISOS_TAB).some((p) => userPermisos.includes(p));
  const puedeVerTab = (clave: keyof typeof PERMISOS_TAB) =>
    isSuperUser || !tieneAlgunVerTab || userPermisos.includes(PERMISOS_TAB[clave]);

  const verRango = puedeVerTab('rango');
  const verCedulas = puedeVerTab('cedulas');
  const verHistorial = puedeVerTab('historial');
  const verOlt = puedeVerTab('olt');

  const [tab, setTab] = useState<0 | 1 | 2 | 3>(0);

  /** Las pestañas visibles, en el orden en que se pintan. */
  const tabsVisibles = useMemo(
    () =>
      [verRango && 0, verCedulas && 1, verHistorial && 2, verOlt && 3].filter(
        (v): v is 0 | 1 | 2 | 3 => v !== false,
      ),
    [verRango, verCedulas, verHistorial, verOlt],
  );

  /**
   * Si la pestaña seleccionada no es visible —al entrar, o si cambian los
   * permisos— se cae a la primera que sí lo sea. Sin esto, quien solo tenga la
   * de OLT entraría a una pantalla sin contenido.
   */
  useEffect(() => {
    if (tabsVisibles.length > 0 && !tabsVisibles.includes(tab)) {
      setTab(tabsVisibles[0]);
    }
  }, [tabsVisibles, tab]);

  const [cantidad, setCantidad] = useState<number>(10);
  const [fechaDesde, setFechaDesde] = useState<string>('2010-01-01');
  const [fechaHasta, setFechaHasta] = useState<string>(todayIso());

  const [cedulasRaw, setCedulasRaw] = useState<string>('');

  const importRango = useImportSoftnetRango();
  const importCedulas = useImportSoftnetCedulas();
  const syncDatosExtendidos = useSyncDatosExtendidos();

  // Ultimo resumen del sync de datos extendidos. Lo guardamos para mostrarlo
  // debajo del header (separado de los resumenes de import, que viven dentro
  // de cada tab).
  const ultimoSyncExtendidos: ISyncDatosExtendidosResponse | null = useMemo(() => {
    return ((syncDatosExtendidos.data as any)?.data as ISyncDatosExtendidosResponse | undefined) ?? null;
  }, [syncDatosExtendidos.data]);

  const handleSyncDatosExtendidos = () => {
    syncDatosExtendidos.mutate();
  };

  const cedulasParsed = useMemo(() => parseCedulas(cedulasRaw), [cedulasRaw]);

  const ultimoResultado: IImportSoftnetResponse | null = useMemo(() => {
    const fromRango = (importRango.data as any)?.data as IImportSoftnetResponse | undefined;
    const fromCedulas = (importCedulas.data as any)?.data as IImportSoftnetResponse | undefined;
    return fromCedulas ?? fromRango ?? null;
  }, [importRango.data, importCedulas.data]);

  const isLoading = importRango.isPending || importCedulas.isPending;

  const handleImportRango = () => {
    if (cantidad <= 0) return;
    importRango.mutate({
      cantidad,
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
    });
  };

  const handleImportCedulas = () => {
    if (cedulasParsed.length === 0) return;
    importCedulas.mutate({ identificaciones: cedulasParsed });
  };

  if (!canVer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          <AlertTitle>Acceso denegado</AlertTitle>
          No tienes permiso para ver el módulo de Migración Softnet. Solicita el permiso <code>migracion.ver</code> a un administrador.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CloudDownloadIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Migración de clientes desde Softnet
          </Typography>
        </Stack>

        {/* Sincronizar datos extendidos: re-consulta a Softnet sobre los
            clientes YA migrados para enriquecer datos_tecnicos detallado,
            equipos, materiales, pagos, deudas, fibra_points e
            servicios_adicionales. NO crea clientes nuevos. */}
        {canEjecutar && (
          <Tooltip
            title={
              'Re-consulta Softnet para los clientes YA migrados y enriquece datos técnicos, equipos, materiales, pagos, deudas y fibra points. ' +
              'Idempotente. También corre automáticamente cada noche (02:30).'
            }
            placement="left"
          >
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={
                  syncDatosExtendidos.isPending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SyncIcon />
                  )
                }
                onClick={handleSyncDatosExtendidos}
                disabled={syncDatosExtendidos.isPending}
              >
                {syncDatosExtendidos.isPending ? 'Sincronizando...' : 'Sincronizar datos extendidos'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Importa clientes, contratos, equipos y tickets históricos desde la API de Softnet a la
        base del módulo de tickets. La operación es <strong>idempotente</strong>: corridas
        repetidas actualizan los registros existentes en lugar de duplicarlos.
      </Typography>

      {/* Resumen ultima corrida del sync de datos extendidos */}
      {ultimoSyncExtendidos && (
        <Alert
          severity={ultimoSyncExtendidos.errores.length > 0 ? 'warning' : 'success'}
          sx={{ mb: 3 }}
          icon={ultimoSyncExtendidos.errores.length > 0 ? <ErrorOutlineIcon /> : <CheckCircleIcon />}
        >
          <AlertTitle>
            Sincronización de datos extendidos completada
            <Chip
              size="small"
              label={`${(ultimoSyncExtendidos.duracion_ms / 1000).toFixed(2)}s`}
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </AlertTitle>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Clientes procesados: ${ultimoSyncExtendidos.clientes_procesados}`} />
            <Chip size="small" label={`Contratos actualizados: ${ultimoSyncExtendidos.contratos_actualizados}`} />
            <Chip size="small" color="success" variant="outlined"
              label={`Equipos +${ultimoSyncExtendidos.equipos_creados}/~${ultimoSyncExtendidos.equipos_actualizados}`} />
            <Chip size="small" color="success" variant="outlined"
              label={`Materiales +${ultimoSyncExtendidos.materiales_creados}/~${ultimoSyncExtendidos.materiales_actualizados}`} />
            <Chip size="small" color="info" variant="outlined"
              label={`Pagos +${ultimoSyncExtendidos.pagos_creados}/~${ultimoSyncExtendidos.pagos_actualizados}`} />
            <Chip size="small" color="warning" variant="outlined"
              label={`Deudas +${ultimoSyncExtendidos.deudas_creadas}/~${ultimoSyncExtendidos.deudas_actualizadas}`} />
            <Chip size="small" color="primary" variant="outlined"
              label={`Fibra Points +${ultimoSyncExtendidos.fibra_points_creados}/~${ultimoSyncExtendidos.fibra_points_actualizados}`} />
            {ultimoSyncExtendidos.errores.length > 0 && (
              <Chip size="small" color="error" label={`Errores: ${ultimoSyncExtendidos.errores.length}`} />
            )}
          </Stack>
          {ultimoSyncExtendidos.errores.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 120, overflowY: 'auto' }}>
              {ultimoSyncExtendidos.errores.slice(0, 10).map((e, i) => (
                <Typography key={i} variant="caption" component="div" color="error">
                  · {e}
                </Typography>
              ))}
              {ultimoSyncExtendidos.errores.length > 10 && (
                <Typography variant="caption" color="text.secondary">
                  ... {ultimoSyncExtendidos.errores.length - 10} errores más
                </Typography>
              )}
            </Box>
          )}
        </Alert>
      )}

      <Paper sx={{ p: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {/* `value` explícito en cada una: al ocultar pestañas los índices se
              correrían y el contenido dejaría de corresponder con la
              seleccionada. Con el value fijo, `tab` sigue significando lo
              mismo se vean cuatro o una. */}
          {verRango && (
            <Tab value={0} icon={<GroupAddIcon />} iconPosition="start" label="Por rango de fechas" />
          )}
          {verCedulas && (
            <Tab value={1} icon={<BadgeIcon />} iconPosition="start" label="Por cédulas / RUC" />
          )}
          {verHistorial && (
            <Tab value={2} icon={<HistoryIcon />} iconPosition="start" label="Historial migrados" />
          )}
          {verOlt && (
            <Tab value={3} icon={<RouterIcon />} iconPosition="start" label="Por OLT / ONT" />
          )}
        </Tabs>

        {tab === 3 && verOlt && <MigracionOltTab canEjecutar={canEjecutarOlt} />}

        {tab === 0 && verRango && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Cómo funciona</AlertTitle>
              Trae <strong>{cantidad}</strong> clientes paginados desde{' '}
              <code>obtener-clientes-base</code> en el rango{' '}
              <strong>{fechaDesde}</strong> → <strong>{fechaHasta}</strong>, y para cada uno
              consulta sus contratos en <code>obtenerContratos</code>.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cantidad de clientes"
                  value={cantidad}
                  onChange={(e) => setCantidad(Math.max(1, Number(e.target.value) || 0))}
                  inputProps={{ min: 1, max: 5000 }}
                  helperText="Máximo 5000"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha desde"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha hasta"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {canEjecutar && (
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    importRango.isPending ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <CloudDownloadIcon />
                    )
                  }
                  disabled={isLoading || cantidad <= 0}
                  onClick={handleImportRango}
                >
                  {importRango.isPending ? 'Migrando...' : `Importar ${cantidad} clientes`}
                </Button>
              </Stack>
            )}
          </Box>
        )}

        {tab === 2 && verHistorial && <HistorialMigradosTab active={tab === 2} />}

        {tab === 1 && verCedulas && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Cómo funciona</AlertTitle>
              Pega cédulas o RUCs separadas por coma, espacio o salto de línea. Para cada
              identificación se consultan sus contratos en Softnet y se importan.
            </Alert>

            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={14}
              label="Identificaciones"
              placeholder={'1724210354\n0101986024001\n1801234567'}
              value={cedulasRaw}
              onChange={(e) => setCedulasRaw(e.target.value)}
              helperText={
                cedulasParsed.length > 0
                  ? `${cedulasParsed.length} identificación(es) detectada(s)`
                  : 'Ingresa al menos una identificación'
              }
            />

            <Divider sx={{ my: 3 }} />

            {canEjecutar && (
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                size="large"
                startIcon={
                  importCedulas.isPending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CloudDownloadIcon />
                  )
                }
                disabled={isLoading || cedulasParsed.length === 0}
                onClick={handleImportCedulas}
              >
                {importCedulas.isPending
                  ? 'Migrando...'
                  : `Importar ${cedulasParsed.length} cédula(s)`}
              </Button>
            </Stack>
            )}
          </Box>
        )}
      </Paper>

      {ultimoResultado && <ResumenMigracion data={ultimoResultado} />}

      {/*
        Dialog modal "Sincronizando datos extendidos...". Aparece SOLO
        mientras la mutation de sync-datos-extendidos esta en curso.
        Como el sync recorre TODOS los clientes ya migrados (puede tardar
        varios minutos), el dialog es prominente y NO permite cerrar para
        que el usuario no dispare otra accion mientras esta corriendo.
      */}
      <Dialog
        open={syncDatosExtendidos.isPending}
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: 2, minWidth: 480 } }}
      >
        <DialogContent>
          <Stack spacing={2.5} alignItems="center" sx={{ py: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <SyncIcon color="primary" sx={{ fontSize: 36 }} />
              <CircularProgress size={32} thickness={5} />
            </Stack>
            <Typography variant="h6" textAlign="center" fontWeight={600}>
              Sincronizando datos extendidos desde Softnet...
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Estamos re-consultando Softnet para <strong>todos los clientes ya migrados</strong>.
              Se actualizan: equipo comercial, datos tecnicos (NAP, puerto, IPv4 publica,
              radio base, PPPoE, potencia, distancia, cliente BRAS, tecnico instalador),
              equipos, materiales, pagos, deudas y fibra points.
            </Typography>
            <Alert severity="warning" variant="outlined" sx={{ width: '100%' }}>
              Esto puede tardar <strong>varios minutos</strong> dependiendo del volumen.
              <strong> No cierres ni recargues</strong> esta ventana.
            </Alert>
            <LinearProgress sx={{ width: '100%', borderRadius: 1, height: 6 }} />
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MigracionSoftnetPage;
